import styled from '@emotion/styled';
import {
  brandNeutralColors,
  colors,
  fontSizes,
  spacing,
  mobileMediaQuery,
} from '@anyroad/theme';
import { Icon } from '@anyroad/icon';
import { TextInput } from '@anyroad/text-input';
import { keyframes } from '@emotion/react';
import { Select } from '@anyroad/select';
import { Button } from '@anyroad/button';
import { Calendar } from './Calendar';

interface InputProps {
  showError?: boolean;
}

interface TimeSelectProps {
  first?: boolean;
}

type LabelProps = {
  showError?: boolean;
};

type LinkTextProps = {
  fontSize?: string;
};

type DateTimeContainerProps = {
  lastSlot?: boolean;
};

const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

export const Container = styled.div`
  text-align: center;
  margin-top: 20px;
`;

export const Text = styled.p`
  font-size: 21px;
  color: ${brandNeutralColors.text.primary};
  margin-bottom: 0px;
  font-weight: 600;
`;

export const NoAvailability = styled.div`
  color: ${brandNeutralColors.text.secondary};
`;

export const LinkText = styled.span<LinkTextProps>`
  color: ${(props) => props.color};
  text-decoration: underline;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '14px')};
  cursor: pointer;
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const Link = styled.a<LinkTextProps>`
  color: ${(props) => props.color};
  text-decoration: underline;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '14px')};
  &:hover {
    color: ${(props) => props.color};
    text-decoration: underline;
  }
`;

export const CheckboxButton = styled.input`
  appearance: none;
  width: 32px;
  height: 32px;
  background-color: ${colors.background.light};
  border: 1px solid ${colors.borderAction};
  align-self: flex-start;
  &:checked {
    fill: white;
    background-color: ${colors.primary};
    border: 1px solid ${colors.primary};
  }
  &:checked + svg {
    opacity: 1;
    width: 15px;
    fill: white;
  }
  &:focus {
    border: 1px solid ${colors.borderFocus};
  }
`;

export const CheckboxText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${brandNeutralColors.text.primary};
  width: 100%;
  flex-basis: 90%;
  @media (max-width: 768px) {
    font-size: 13px;
    flex-basis: 85%;
  }
`;

export const Form = styled.form`
  align-items: center;
  color: ${colors.text.primary};
  display: flex;
  flex-direction: column;
  font-size: ${fontSizes.medium};
  gap: ${spacing.xlarge};
`;

export const Header = styled.h3`
  font-size: 24px;
  font-weight: 400;
  color: ${brandNeutralColors.text.primary};
  margin-bottom: 0;
  margin-top: 10px;
`;

export const SubHeader = styled.p`
  font-size: 14px;
  text-align: center;
  margin-top: -15px;
  color: ${brandNeutralColors.text.primary};
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const InputField = styled(TextInput)<InputProps>`
  align-self: flex-end;
  font-size: 14px;
  background: ${(props) => props.showError && colors.errorBackground};
  border: ${(props) => props.showError && `1px solid ${colors.errorBorder}`};
  border-left: ${(props) =>
    props.showError && `3px solid ${colors.background.error}`};
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const CheckboxContainer = styled.div<InputProps>`
  background: ${(props) => props.showError && colors.errorBackground};
  border: ${(props) => props.showError && `1px solid ${colors.errorBorder}`};
  border-left: ${(props) =>
    props.showError && `5px solid ${colors.background.error}`};
  padding: ${(props) => props.showError && '10px'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  svg {
    opacity: 0;
    width: 15px;
    position: relative;
    right: 8px;
    bottom: 14px;
    pointer-events: none;
  }
`;

export const NameContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: end;
  width: 100%;
`;

export const DateSelectContainer = styled.span`
  width: 48%;
`;

export const Label = styled.label<LabelProps>`
  font-size: 16px;
  color: ${brandNeutralColors.text.primary};
  line-height: 18px;
  margin-bottom: 4px;
  animation-duration: 0.2s;
  animation-iteration-count: 4;
  animation-name: ${(props) => (props.showError ? shakeAnimation : 'none')};
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const DateBasicSelect = styled(Calendar)`
  height: 44px;
`;

export const TimeSelectContainer = styled.div<TimeSelectProps>`
  width: ${(props) => (props.first ? '48%' : '40%')};
`;

export const TimeBasicSelect = styled(Select)`
  margin-top: 8px;
  height: 44px;
  font-size: 14px;
  color: ${brandNeutralColors.text.placeholder};
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const SelectWrapper = styled.div`
  margin: 8px 0 12px;
  width: 100%;
`;

export const DateTimeContainer = styled.div<DateTimeContainerProps>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${(props) => (props.lastSlot ? '-20px' : '-40px')};
  align-items: center;
`;

export const RemoveDateTimeContainer = styled.div``;
export const RemoveIcon = styled(Icon)({
  flexShrink: 0,
  height: '20px',
  width: '20px',
});

export const SingleAvailableTime = styled.div`
  font-size: 16px;
  margin-top: 16px;
  font-weigth: 200;
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const AddSlot = styled.p<any>`
  align-self: flex-start;
  font-size: 16px;
  text-decoration: underline;
  cursor: pointer;
  margin-bottom: 0;
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const SpinnerContainer = styled.div`
  text-align: center;
`;

export const SuccessModalText = styled.p`
  text-align: center;
  font-size: 14px;
`;

export const Content = styled.div`
  align-items: center;
  color: ${colors.text.primary};
  display: flex;
  flex-direction: column;
  font-size: ${fontSizes.medium};
  gap: ${spacing.medium};
`;

export const WaitlistText = styled.div`
  color: ${colors.text.secondary};
`;

export const StyledLabel = styled.label<{ error: boolean }>`
  display: block;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 150%;
  width: initial;
  font-size: 16px;
  margin: 0 0 8px;
  color: ${(props) =>
    props.error ? colors.background.error : brandNeutralColors.text.primary};
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const PhoneContainer = styled.div({
  width: '100%',
});

export const JoinWaitlistButton = styled(Button)({
  textTransform: 'capitalize',
  fontSize: '16px',
  [mobileMediaQuery]: {
    width: '100%',
    fontSize: '15px',
  },
});

export const SuccessButton = styled(Button)({
  [mobileMediaQuery]: {
    width: '100%',
  },
});

export const Placeholder = styled.div`
  color: ${brandNeutralColors.text.placeholder};
`;

export const DropdownIcon = styled.i`
  color: ${colors.text.icon};
`;

export const CheckboxWrapper = styled.div`
  position: relative;
  align-self: flex-start;
`;
